import { Draggable } from "@hello-pangea/dnd";
import { ActionIcon, Flex, Grid, rem, Text, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCopy,
  IconEdit,
  IconGripVertical,
  IconListTree,
  IconTrash,
} from "@tabler/icons-react";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { StyledGridCol } from "src/components/pages/Summary/VerifyEntries/VerifyEntries.styled";
import { usePostApi } from "src/hooks";
import { Notification } from "src/utils/Notification";

import ChecklistDescription from "../../../../../shared/ChecklistDescription";
import ConfirmationButton from "../../../../../shared/ConfirmationButton/ConfirmationButton";
import CheckListModal from "../CheckListForm/CheckListModal";
import { CheckListType, type ICheckListItem } from "../CheckLists.types";
import { useChecklistsContext } from "../CheckListsContext";
import styles from "./CheckListTableRow.module.scss";

interface Props {
  row: ICheckListItem;
  item: any;
  index: number;
}

const CheckListTableRow = ({ row, item, index }: Props) => {
  const { post } = usePostApi();
  const [opened, { open, close }] = useDisclosure();
  const { logbooks, fetchChecklists } = useChecklistsContext();
  const { lbid } = useParams();
  const [itemsVisible, setItemsVisible] = useState(false);
  const [isItem, setIsItem] = useState<null | number>(null);

  const getLbname = useCallback(() => {
    if (typeof row.lbid === "string") {
      return logbooks.find(l => l.value === row.lbid)?.label;
    }
    return row.lbid
      .map(id => logbooks.find(l => l.value === id)?.label)
      .join(", ");
  }, [logbooks, row.lbid]);

  const defaultValues = useMemo(
    () => ({
      cid: row.cid,
      name: row.name,
      desc: row.description,
      lbid: row.lbid === "*" ? ["*"] : row.lbid,
      type: row.data && (row.data.type as CheckListType),
      list: row.data && row.data.list,
      sort: row.sort,
    }),
    [row],
  );

  const handleRemove = useCallback(async () => {
    const cid = row.cid;
    try {
      await post({
        request: "archive-checklists",
        body: { params: { cid } },
        onSuccess: () => {
          fetchChecklists({ params: { lbid, archived: 0 } }, true);
          Notification(
            "Checklist removed",
            "Checklist has been deleted successfully",
            "green",
          );
        },
      });
    } catch (error) {
      Notification("Error", "There was an error deleting the checklist", "red");
    }
  }, [post, fetchChecklists, row.cid]);

  const handleSave = useCallback(
    async (params: Record<string, any>) => {
      await post({
        request: "save-checklist",
        body: { params },
        onSuccess: () => {
          fetchChecklists({ params: { lbid, archived: 0 } }, true);
          Notification(
            "Success",
            "Check List has been copied successfully",
            "green",
          );
        },
      });
    },
    [row, post, fetchChecklists],
  );

  const handleCopy = () => {
    const { sort, lbid, yachtid, data, description, name } = row;
    const params = {
      name: `${name} (Copy)`,
      desc: description,
      sort,
      lbid,
      data,
      yachtid,
    };
    handleSave(params);
  };

  const handleRemoveChild = (index: number) => {
    const { sort, lbid, yachtid, data, name, description } = row;
    const params = {
      name,
      desc: description,
      sort,
      lbid,
      data: { ...data, list: data.list.filter((_, i) => i !== index) },
      yachtid,
      cid: row.cid,
    };
    handleSave(params);
  };
  return (
    <Fragment>
      <Draggable key={item.symbol} index={index} draggableId={item.symbol}>
        {provided => (
          <Grid
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={styles.row}>
            <StyledGridCol span={1} justify="center" align="center">
              <Flex
                {...provided.dragHandleProps}
                justify="center"
                align="center">
                <IconGripVertical
                  style={{ width: rem(18), height: rem(18) }}
                  stroke={1.5}
                />
              </Flex>
            </StyledGridCol>
            <StyledGridCol span={2} align="center" textAlign="left">
              {row.name}
            </StyledGridCol>
            <StyledGridCol span={3} align="center" textAlign="left">
              {getLbname()}
            </StyledGridCol>
            <StyledGridCol span={3} align="center" textAlign="left">
              <ChecklistDescription desc={row.description} />
            </StyledGridCol>
            <StyledGridCol span={1} align="center" textAlign="left">
              {row.version}
            </StyledGridCol>
            <StyledGridCol justify="" align="center" span={2}>
              <Flex w="100%" justify="end" align="center" gap="sm">
                {row.data.type === CheckListType.Group && (
                  <Tooltip label="Show group items" position="bottom" withArrow>
                    <Flex align="center">
                      <Text size="sm">({row.data.list.length})</Text>
                      <ActionIcon
                        onClick={() => {
                          setItemsVisible(!itemsVisible);
                        }}>
                        <IconListTree />
                      </ActionIcon>
                    </Flex>
                  </Tooltip>
                )}

                <Tooltip label="Copy" position="bottom" withArrow>
                  <ActionIcon onClick={handleCopy}>
                    <IconCopy />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Edit" position="bottom" withArrow>
                  <ActionIcon onClick={open}>
                    <IconEdit />
                  </ActionIcon>
                </Tooltip>
                <ConfirmationButton
                  buttonProps={{ variant: "none", color: "red" }}
                  modalText={
                    "You are about to delete this Check List, please confirm you want to proceed."
                  }
                  onConfirm={handleRemove}>
                  <Tooltip label="Delete" position="bottom" withArrow>
                    <IconTrash color="#ff6b6b" />
                  </Tooltip>
                </ConfirmationButton>
              </Flex>
            </StyledGridCol>
            {row.data.type === CheckListType.Group && itemsVisible && (
              <>
                <Grid.Col span={1}></Grid.Col>
                <Grid.Col span={10} className={styles.groupItemsPanel}>
                  <Grid className={styles.groupItemsContainer}>
                    <Grid.Col span={9}>
                      <Text size="sm">Name</Text>
                    </Grid.Col>
                    <Grid.Col span={3}>
                      <Text size="sm">Actions</Text>
                    </Grid.Col>
                    {row.data.list.map((sub, i) => (
                      <Fragment key={`checklist-item-${i}`}>
                        <Grid.Col span={9}>
                          <p>{sub.name}</p>
                        </Grid.Col>
                        {/* <Grid.Col span={5}> */}
                        {/*  <p */}
                        {/*    style={{ */}
                        {/*      fontSize: "14px", */}
                        {/*      maxHeight: "50px", */}
                        {/*      overflowY: "auto", */}
                        {/*    }}> */}
                        {/*    {sub.desc} */}
                        {/*  </p> */}
                        {/* </Grid.Col> */}
                        <Grid.Col span={3}>
                          <Flex align="center">
                            <Tooltip label="Edit" position="bottom" withArrow>
                              <ActionIcon
                                onClick={() => {
                                  setIsItem(i);
                                  open();
                                }}>
                                <IconEdit size={20} />
                              </ActionIcon>
                            </Tooltip>
                            <ConfirmationButton
                              buttonProps={{ variant: "none", color: "red" }}
                              modalText={
                                "You are about to delete this Check List, please confirm you want to proceed."
                              }
                              onConfirm={() => handleRemoveChild(i)}>
                              <Tooltip
                                label="Delete"
                                position="bottom"
                                withArrow>
                                <IconTrash color="#ff6b6b" size={20} />
                              </Tooltip>
                            </ConfirmationButton>
                          </Flex>
                        </Grid.Col>
                      </Fragment>
                    ))}
                  </Grid>
                </Grid.Col>
              </>
            )}
          </Grid>
        )}
      </Draggable>
      {opened && (
        <CheckListModal
          key={defaultValues.list.map((i: any) => i.id).join("")}
          opened={opened}
          onClose={() => {
            close();
            setTimeout(() => {
              setIsItem(null);
            }, 100);
          }}
          defaultValues={defaultValues}
          isEdit
          isItem={isItem}
        />
      )}
    </Fragment>
  );
};

export default CheckListTableRow;
